import type { StreamLainAnimationHandle } from '@blocksuite/affine/shared/utils';

export interface StreamLainLoaderSprite {
  durationMs: number;
  columns: number;
  rows: number;
  frames: readonly number[];
  fullFrame: number;
}

declare global {
  interface Window {
    __streamlainBrandLoaderBitmaps?: Map<
      string,
      { image: HTMLImageElement; ready: Promise<void> }
    >;
  }
}
// Share the two theme bitmaps between pre-bundle boot and all app loader hosts.
const decodedSprites =
  typeof window === 'undefined'
    ? new Map<string, { image: HTMLImageElement; ready: Promise<void> }>()
    : (window.__streamlainBrandLoaderBitmaps ??= new Map());

/** Decode CSS URL tokens before assigning them to the DOM Image API. */
export function readStreamLainLoaderBitmapUrl(css: string): string {
  const match = /^url\(\s*(["']?)([\s\S]*?)\1\s*\)$/i.exec(css.trim());
  if (!match) return '';
  return match[2].replace(
    /\\(?:([0-9a-f]{1,6})(?:\r\n|[ \t\n\r\f])?|(\r\n|[\n\r\f])|([\s\S]))/gi,
    (
      _escape,
      hex: string | undefined,
      _lineBreak,
      character: string | undefined
    ) => {
      if (hex === undefined) return character ?? '';
      const codePoint = Number.parseInt(hex, 16);
      return String.fromCodePoint(
        codePoint === 0 ||
          codePoint > 0x10ffff ||
          (codePoint >= 0xd800 && codePoint <= 0xdfff)
          ? 0xfffd
          : codePoint
      );
    }
  );
}

/** Shared DOM player for React loaders and the pre-bundle HTML splash. */
export function createStreamLainBrandLoaderPlayer(
  root: HTMLElement,
  sprite: StreamLainLoaderSprite,
  controller: {
    isEnabled: (element: Element) => boolean;
    playLoop: (
      element: Element,
      frames: Keyframe[],
      durationMs: number,
      startTime?: number | null
    ) => StreamLainAnimationHandle;
  }
) {
  const sheet = root.querySelector<HTMLElement>(
    '[data-streamlain-loader-sheet]'
  );
  if (!sheet) throw new Error('Brand loader requires its sprite sheet');
  const view = root.ownerDocument.defaultView;
  if (!view) throw new Error('Brand loader requires a browsing context');
  // Moving a large atlas beneath a clip exposes new raster tiles during load.
  // Keep each unique crop stationary in its own small compositor layer instead;
  // native opacity steps select the exact same pre-rendered timeline.
  const changes = sprite.frames.flatMap((frame, index) =>
    index === 0 || sprite.frames[index - 1] !== frame
      ? [{ frame, offset: index / sprite.frames.length }]
      : []
  );
  const frames = [...new Set(sprite.frames)].map(frame => {
    const node = root.ownerDocument.createElement('span');
    node.setAttribute('data-streamlain-loader-frame', String(frame));
    node.style.backgroundSize = `${sprite.columns * 100}% ${sprite.rows * 100}%`;
    node.style.backgroundPosition = `${sprite.columns > 1 ? ((frame % sprite.columns) * 100) / (sprite.columns - 1) : 0}% ${sprite.rows > 1 ? (Math.floor(frame / sprite.columns) * 100) / (sprite.rows - 1) : 0}%`;
    const keyframes: Keyframe[] = [];
    for (const change of changes) {
      const opacity = change.frame === frame ? 1 : 0;
      if (keyframes.at(-1)?.opacity !== opacity)
        keyframes.push({
          offset: change.offset,
          opacity,
          easing: 'steps(1, end)',
        });
    }
    keyframes.push({ offset: 1, opacity: sprite.frames[0] === frame ? 1 : 0 });
    sheet.append(node);
    return { node, keyframes };
  });
  let enabled = false;
  let visible = typeof IntersectionObserver === 'undefined';
  let disposed = false;
  let bitmapReady = false;
  let bitmapFailed = false;
  let bitmapUrl = '';
  let animations: StreamLainAnimationHandle[] | undefined;
  const motion = view.matchMedia('(prefers-reduced-motion: reduce)');
  const stop = () => {
    const previous = animations;
    animations = undefined;
    previous?.forEach(handle => handle.cancel());
    root.removeAttribute('data-streamlain-loader-active');
    frames.forEach(({ node }) => {
      node.style.willChange = 'auto';
    });
  };
  const refresh = () => {
    if (disposed) return;
    // Animated loaders remain blank until frame zero can play. A static logo
    // is reserved for disabled motion or an unavailable bitmap.
    const pending =
      enabled &&
      controller.isEnabled(sheet) &&
      typeof sheet.animate === 'function' &&
      !bitmapFailed;
    root.toggleAttribute('data-streamlain-loader-pending', pending);
    const active =
      enabled &&
      visible &&
      !root.ownerDocument.hidden &&
      bitmapReady &&
      controller.isEnabled(sheet) &&
      typeof sheet.animate === 'function';
    if (!active) {
      stop();
      return;
    }
    if (animations) return;
    const timelineTime = root.ownerDocument.timeline.currentTime;
    const startTime =
      typeof timelineTime === 'number' ? timelineTime : undefined;
    const handles = frames.map(({ node, keyframes }) => {
      node.style.willChange = 'opacity';
      return controller.playLoop(node, keyframes, sprite.durationMs, startTime);
    });
    animations = handles;
    root.setAttribute('data-streamlain-loader-active', 'true');
    void Promise.all(handles.map(handle => handle.finished)).then(() => {
      if (animations === handles) stop();
    });
  };
  const warmBitmap = (url: string) => {
    let cached = decodedSprites.get(url);
    if (!cached) {
      const image = new view.Image();
      image.src = url;
      const ready =
        typeof image.decode === 'function'
          ? image.decode()
          : new Promise<void>((resolve, reject) => {
              image.onload = () => resolve();
              image.onerror = () =>
                reject(new Error('Brand loader bitmap unavailable'));
            });
      cached = { image, ready };
      decodedSprites.set(url, cached);
      void ready.catch(() => decodedSprites.delete(url));
    }
    return cached;
  };
  const prepare = () => {
    if (disposed) return;
    const computed = view.getComputedStyle(root);
    const url = readStreamLainLoaderBitmapUrl(
      computed.getPropertyValue('--sl-brand-loader-url')
    );
    if (url) void warmBitmap(url).ready.catch(() => {});
    // Warm both public theme atlases once, including hidden document masks.
    // Switching a bright canvas surface never starts a new decode during load.
    for (const name of ['--sl-brand-loader-color', '--sl-brand-loader-white']) {
      const candidate = readStreamLainLoaderBitmapUrl(
        computed.getPropertyValue(name)
      );
      if (candidate) void warmBitmap(candidate).ready.catch(() => {});
    }
    if (!url || url === bitmapUrl) {
      refresh();
      return;
    }
    stop();
    bitmapReady = false;
    bitmapFailed = false;
    bitmapUrl = url;
    refresh();
    const cached = warmBitmap(url);
    void cached.ready.then(
      () => {
        if (disposed || url !== bitmapUrl) return;
        bitmapReady = true;
        refresh();
      },
      () => {
        if (disposed || url !== bitmapUrl) return;
        bitmapFailed = true;
        refresh();
      }
    );
  };
  const intersection =
    typeof IntersectionObserver === 'undefined'
      ? undefined
      : new IntersectionObserver(entries => {
          visible = entries.some(entry => entry.isIntersecting);
          refresh();
        });
  intersection?.observe(root);
  const theme = new MutationObserver(prepare);
  theme.observe(root.ownerDocument.documentElement, {
    attributes: true,
    attributeFilter: [
      'data-theme',
      'data-sl-boot-theme',
      'data-streamlain-animations',
    ],
  });
  const canvas = root.closest('[data-edgeless-preset]');
  if (canvas)
    theme.observe(canvas, {
      attributes: true,
      attributeFilter: ['data-edgeless-preset'],
    });
  root.ownerDocument.addEventListener('visibilitychange', refresh);
  motion.addEventListener('change', refresh);
  prepare();
  return {
    setEnabled(value: boolean) {
      enabled = value;
      refresh();
    },
    refresh,
    dispose() {
      disposed = true;
      stop();
      root.removeAttribute('data-streamlain-loader-pending');
      intersection?.disconnect();
      theme.disconnect();
      root.ownerDocument.removeEventListener('visibilitychange', refresh);
      motion.removeEventListener('change', refresh);
      sheet.replaceChildren();
    },
  };
}
